// src/router/index.ts
import {
  createRouter,
  createWebHashHistory,
  type RouteRecordRaw,
  type NavigationGuardNext,
  type RouteLocationNormalized,
} from 'vue-router';
import { useDataStore } from '@/store/useDataStore';
import { findByAnyId } from '@/utils/idLookup';

// Minimal type definitions for router usage
interface EntityWithUrlId {
  id: string;
  url_id?: string;
}

// Lazy-loaded top-level views
const Home = () => import('@/views/HomeView.vue');
const Canvas = () => import('@/views/CanvasView.vue');
const Contacts = () => import('@/views/ContactsView.vue');
const Notifications = () => import('@/views/NotificationsView.vue');
const Saved = () => import('@/views/SavedView.vue');
const Calendar = () => import('@/views/CalendarView.vue');

// Lazy-loaded child views for Home
const Launchpad = () => import('@/views/home/LaunchpadView.vue');
const Inbox = () => import('@/views/home/InboxView.vue');
const Threads = () => import('@/views/home/ThreadsView.vue');
const Scheduled = () => import('@/views/home/ScheduledView.vue');
const Feed = () => import('@/views/home/FeedView.vue');
const CCOverview = () => import('@/views/home/ContactCentersView.vue');
const VoiceCCDept = () => import('@/views/home/VoiceCCDeptView.vue');
const DigitalCC = () => import('@/views/home/DigitalCCView.vue');

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: Home,
    meta: { title: '' },
    children: [
      { path: '', redirect: '/inbox' },
      {
        path: 'inbox',
        name: 'Inbox',
        component: Inbox,
        meta: {
          title: 'Inbox',
          linkId: '1a431423-8e8b-41e7-9787-8e876168c9a1',
          urlId: 'GkMUI46LQec',
          slug: 'inbox',
        },
      },
      {
        path: 'threads',
        name: 'Threads',
        component: Threads,
        meta: {
          title: 'Threads',
          linkId: '0d13ccb1-4aff-4ab9-9bf5-5fc3599e7ad5',
          urlId: 'DRPMsUr_Srk',
          slug: 'threads',
        },
      },
      {
        path: 'scheduled',
        name: 'Drafts & Scheduled',
        component: Scheduled,
        meta: {
          title: 'Scheduled',
          linkId: '1eef5cdf-95ea-41ec-bce9-947fab87ef5c',
          urlId: 'Hu9c35XqQew',
          slug: 'scheduled',
        },
      },
      {
        path: 'cc-list',
        name: 'Contact Centers',
        component: CCOverview,
        meta: {
          title: 'Contact Centers',
          linkId: 'e0f624c0-565b-4dfe-9197-e8c33b23c8c3',
          urlId: '4PYkwFZbTf4',
          slug: 'contact-centers',
        },
      },
      {
        path: 'voice-cc/:slug?',
        name: 'VoiceCCDept',
        component: VoiceCCDept,
        props: true,
        meta: { title: 'Voice CC' },
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          if (!to.params.slug) return next();

          const dataStore = useDataStore();
          const centers = dataStore.contactCentersData?.centers || [];

          // Use utility function to find by any ID
          const center = findByAnyId(centers, to.params.slug as string);

          if (center) {
            to.params.id = center.id;
            return next();
          }

          next();
        },
      },
      {
        path: 'digital-cc/:slug?',
        name: 'DigitalCC',
        component: DigitalCC,
        props: true,
        meta: { title: 'Digital CC' },
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          if (!to.params.slug) return next();

          const dataStore = useDataStore();
          const centers = dataStore.contactCentersData?.centers || [];

          // Use utility function to find by any ID
          const center = findByAnyId(centers, to.params.slug as string);

          if (center) {
            to.params.id = center.id;
            return next();
          }

          next();
        },
      },
      {
        path: 'feed/:slug?',
        name: 'Feed',
        component: Feed,
        props: true,
        meta: { title: 'Feed' },
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          // If no slug is provided, just continue
          if (!to.params.slug) {
            return next();
          }

          // Get the data store to look up the entity by url_id
          const dataStore = useDataStore();

          // Look for the entity with this url_id across different entity types
          const contacts = dataStore.contactsData?.contacts || [];
          const channels = dataStore.channelsData?.channels || [];
          const groups = dataStore.groupMessagesData?.group_messages || [];

          // Find the entity with matching url_id (this is what we actually use)
          const entity =
            contacts.find((c: EntityWithUrlId) => c.url_id === to.params.slug) ||
            channels.find((c: EntityWithUrlId) => c.url_id === to.params.slug) ||
            groups.find((g: EntityWithUrlId) => g.url_id === to.params.slug);

          // If found, set the id parameter for the component to use
          if (entity) {
            to.params.id = entity.id;
            return next();
          }

          // If not found, the component will handle the not-found case
          next();
        },
      },
      {
        path: 'launchpad',
        name: 'Launchpad',
        component: Launchpad,
        meta: { title: 'Launchpad' },
      },
    ],
  },
  {
    path: '/canvas',
    name: 'Canvas',
    component: Canvas,
    meta: { title: 'Canvas' },
  },
  {
    path: '/contacts',
    name: 'Contacts',
    component: Contacts,
    meta: { title: 'Contacts' },
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: Notifications,
    meta: { title: 'Notifications' },
  },
  {
    path: '/saved',
    name: 'Saved',
    component: Saved,
    meta: { title: 'Saved Items' },
  },
  {
    path: '/calendar',
    name: 'Calendar',
    component: Calendar,
    meta: { title: 'Calendar' },
  },
];

const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0 };
  },
});

declare global {
  interface Window {
    __setUnreadCount: (n: number) => void;
  }
}

let inboxCount = 0;
let currentRoute: RouteLocationNormalized | null = null;

function formatInboxTitle() {
  const countText = inboxCount > 99 ? '99+' : inboxCount;
  return `Inbox (${countText}) - Dialpad`;
}

function updateTitleIfNeeded() {
  // Only update title if we're currently on the inbox route
  if (currentRoute?.name === 'Inbox') {
    document.title = formatInboxTitle();
  }
}

window.__setUnreadCount = (n: number) => {
  inboxCount = n;
  updateTitleIfNeeded();
};

router.afterEach(to => {
  currentRoute = to;

  if (to.name === 'Inbox') {
    document.title = formatInboxTitle();
    return;
  }

  if (to.name === 'Feed') {
    document.title = 'Feed - Dialpad';
    return;
  }

  const viewTitle = to.meta?.title || 'App';
  document.title = `${viewTitle} - Dialpad`;
});

export default router;
